import { useMemo, useState } from "react"
import { useNavigate, useParams, useLocation } from "react-router-dom"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useWorkspaceSafe } from "@/lib/contexts/WorkspaceContext"
import { useQuery } from "@tanstack/react-query"
import { useAuth } from "@/lib/contexts/AuthContext"
import Favicon, { isDomainLike } from "@/components/shared/Favicon"
import { AVATAR_COLORS, SiteGradientAvatar } from "@/components/dashboard/hub/UsageChart"
import { getAllWorkspaces } from "@/lib/api/workspaces"
import { getAllProperties } from "@/lib/api/api"
import type { Workspace as WorkspaceType } from "@/lib/types/workspace"
import { cn } from "@/lib/utils"
import { generateSlugId, parseSlugId } from "@/lib/routing/slug-id"

import { HugeiconsIcon } from "@hugeicons/react"
import {
  Tick02Icon,
  PlusSignIcon,
  GridViewIcon,
  Settings01Icon,
  Search01Icon,
  Layers01Icon,
} from "@hugeicons/core-free-icons"

interface WorkspaceSwitcherProps {
  isCollapsed?: boolean
}

interface WorkspaceDropdownItem {
  id: string
  name: string
  slug?: string
  properties?: Array<{ propertyId: string; name: string; domain: string }>
}

function getDomainColor(domain: string): string {
  const hash = domain.split('').reduce((a, c) => a + c.charCodeAt(0), 0)
  return AVATAR_COLORS[hash % AVATAR_COLORS.length]
}

/** Prefer a real hostname for labels — names often duplicate the workspace. */
function sitePrimaryLabel(property?: { name?: string; domain?: string } | null): string {
  if (!property) return 'Site'
  const domain = (property.domain || '').trim()
  const name = (property.name || '').trim()
  if (domain) return domain
  return name || 'Untitled'
}

function labelsMatch(a?: string | null, b?: string | null): boolean {
  if (!a || !b) return false
  return a.trim().toLowerCase() === b.trim().toLowerCase()
}

/** Site favicon tile — elevated hit target for the switcher trigger. */
const SiteFavicon = ({ domain, size = 28 }: { domain: string; size?: number }) => {
  const color = getDomainColor(domain)
  return (
    <div
      className="shrink-0 rounded-md overflow-hidden ring-1 ring-border/40 bg-card flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <Favicon
        domain={domain}
        size={size - 2}
        renderFallback={() => (
          <SiteGradientAvatar domain={domain} color={color} size={size - 2} />
        )}
      />
    </div>
  )
}

/** Muted workspace / aggregate mark — not a user avatar, not orange brand. */
const WorkspaceMark = ({
  name,
  size = 28,
}: {
  name: string
  size?: number
}) => {
  const initials = (name || 'W')
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'W'

  return (
    <div
      className="shrink-0 rounded-md flex items-center justify-center font-medium text-muted-foreground bg-muted/50 border border-border/40"
      style={{ width: size, height: size, fontSize: Math.max(10, size * 0.34) }}
      aria-hidden
    >
      {initials}
    </div>
  )
}

/** Account-wide mark for /dashboard — ink tile with the brand bars (Paper "App v3"). */
const AccountMark = ({ size = 28 }: { size?: number }) => (
  <div
    className="shrink-0 rounded-lg flex items-center justify-center bg-[#171717] dark:ring-1 dark:ring-white/10"
    style={{ width: size, height: size }}
    aria-hidden
  >
    <svg width={Math.round(size * 0.57)} height={Math.round(size * 0.57)} viewBox="0 0 20 20" aria-hidden="true">
      <rect x="3" y="11" width="3.5" height="6" rx="1.2" fill="#737373" />
      <rect x="8.25" y="7" width="3.5" height="10" rx="1.2" fill="#D4D4D4" />
      <rect x="13.5" y="3" width="3.5" height="14" rx="1.2" className="fill-primary" />
    </svg>
  </div>
)

/** Compact row favicon for dropdown lists. */
const RowFavicon = ({ domain, size = 16 }: { domain: string; size?: number }) => {
  if (!domain || !isDomainLike(domain)) {
    return (
      <div
        className="shrink-0 rounded-[4px] flex items-center justify-center bg-muted/40 border border-border/30"
        style={{ width: size, height: size }}
      >
        <HugeiconsIcon icon={Layers01Icon} size={10} className="text-muted-foreground/60" />
      </div>
    )
  }
  const color = getDomainColor(domain)
  return (
    <div
      className="shrink-0 rounded-[4px] overflow-hidden ring-1 ring-border/30 bg-card"
      style={{ width: size, height: size }}
    >
      <Favicon
        domain={domain}
        size={size}
        renderFallback={() => (
          <SiteGradientAvatar domain={domain} color={color} size={size} />
        )}
      />
    </div>
  )
}

export default function WorkspaceSwitcher({ isCollapsed = false }: WorkspaceSwitcherProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const { token } = useAuth()
  const workspaceContext = useWorkspaceSafe()

  const isPropertyRoute = !!params.propertySlugId && !workspaceContext
  const currentPropertyIdFromRoute = isPropertyRoute && params.propertySlugId
    ? parseSlugId(params.propertySlugId).id
    : null

  // Account-wide /dashboard — never pretend a workspace is scoped.
  const isAccountRoute = location.pathname.startsWith('/dashboard') && !params.workspaceId && !params.propertySlugId

  const [search, setSearch] = useState('')

  const currentSubPath = useMemo(() => {
    if (!params.propertySlugId) return '/traffic'
    const base = `/p/${params.propertySlugId}`
    const sub = location.pathname.slice(base.length) || '/traffic'
    return sub.startsWith('/') ? sub : `/${sub}`
  }, [params.propertySlugId, location.pathname])

  const rawWorkspaceId = params.workspaceId ? parseSlugId(params.workspaceId).id : undefined
  const currentWorkspaceId = rawWorkspaceId || workspaceContext?.workspace?.workspaceId
  const currentWorkspace = workspaceContext?.workspace

  const selectedPropertyId = workspaceContext?.selectedPropertyId ?? null
  const contextEnabledProperties = workspaceContext?.enabledProperties
  const isAggregateMode = workspaceContext?.isAggregateMode ?? false
  const setSelectedPropertyId = workspaceContext?.setSelectedPropertyId

  const { data: workspacesData = [] } = useQuery<WorkspaceType[]>({
    queryKey: ['workspaces', token],
    queryFn: async () => {
      if (!token) return []
      return getAllWorkspaces(token)
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  })

  const workspaces: WorkspaceDropdownItem[] = workspacesData.map(w => ({
    id: w.workspaceId,
    name: w.name || 'Untitled Workspace',
    properties: w.properties,
  }))

  const { data: allPropertiesData } = useQuery({
    queryKey: ['all-properties-for-switcher', token],
    queryFn: async () => {
      const response = await getAllProperties(token!)
      return response?.properties || []
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  })

  const workspacePropertyIds = useMemo(() => {
    const ids = new Set<string>()
    workspacesData.forEach(w => w.properties?.forEach(p => ids.add(p.propertyId)))
    return ids
  }, [workspacesData])

  const unassignedProperties = useMemo(() => {
    if (!isPropertyRoute || !allPropertiesData) return []
    return allPropertiesData.filter((p: any) => !workspacePropertyIds.has(p.propertyId))
  }, [isPropertyRoute, allPropertiesData, workspacePropertyIds])

  const currentUnassignedProperty = useMemo(() =>
    unassignedProperties.find((p: any) => p.propertyId === currentPropertyIdFromRoute) ?? null,
    [unassignedProperties, currentPropertyIdFromRoute]
  )

  const currentWorkspaceFromList = workspaces.find(w => w.id === currentWorkspaceId)
    || (params.workspaceId ? workspaces.find(w => params.workspaceId!.endsWith(`-${w.id}`) || params.workspaceId === w.id) : undefined)
    || (!params.workspaceId && !isPropertyRoute && !isAccountRoute && workspaces.length > 0 ? workspaces[0] : undefined)

  const totalSiteCount = useMemo(
    () => workspacesData.reduce((sum, w) => sum + (w.properties?.length || 0), 0),
    [workspacesData]
  )

  const workspaceName = currentWorkspace?.name || currentWorkspaceFromList?.name || 'Workspace'

  const enabledProperties = useMemo(() => {
    const fromContext = contextEnabledProperties ?? []
    const raw = fromContext.length > 0
      ? fromContext
      : (currentWorkspaceFromList?.properties?.filter(p => (p as any).enabled !== false) ?? [])
    const seen = new Set<string>()
    return raw.filter(p => {
      if (seen.has(p.propertyId)) return false
      seen.add(p.propertyId)
      return true
    })
  }, [contextEnabledProperties, currentWorkspaceFromList?.properties])

  const filteredProperties = useMemo(() => {
    if (!search) return enabledProperties
    const q = search.toLowerCase()
    return enabledProperties.filter(p =>
      (p.domain || '').toLowerCase().includes(q) ||
      (p.name || '').toLowerCase().includes(q)
    )
  }, [enabledProperties, search])

  // Single-site workspaces auto-resolve to that site for display.
  const selectedProperty = enabledProperties.find(p => p.propertyId === selectedPropertyId)
    || (enabledProperties.length === 1 ? enabledProperties[0] : undefined)

  // Viewing a specific site vs workspace / All Sites aggregate.
  const viewingSite = Boolean(
    !isAccountRoute && (
      isPropertyRoute
        ? currentUnassignedProperty
        : selectedProperty && !isAggregateMode
    )
  )

  /**
   * Trigger copy hierarchy:
   * - Site scoped → primary domain, secondary workspace (omit if duplicate)
   * - Workspace / All Sites → primary workspace, secondary "N sites"
   * - Account → "All workspaces" + counts
   */
  const trigger = useMemo(() => {
    if (isAccountRoute) {
      return {
        primary: 'All workspaces',
        secondary: `${totalSiteCount} site${totalSiteCount === 1 ? '' : 's'} · ${workspaces.length} workspace${workspaces.length === 1 ? '' : 's'}`,
        domain: null as string | null,
        kind: 'account' as const,
      }
    }

    if (isPropertyRoute) {
      // A /p/ route can be any site (the hub's "Site settings" links there),
      // not only an unassigned one: name its workspace when it has one.
      const owner = currentUnassignedProperty
        ? null
        : workspacesData.find((w) => w.properties?.some((p: any) => p.propertyId === currentPropertyIdFromRoute))
      const property = currentUnassignedProperty
        ?? owner?.properties?.find((p: any) => p.propertyId === currentPropertyIdFromRoute)
      const primary = sitePrimaryLabel(property as any)
      const ownerName = owner?.name || null
      return {
        primary,
        secondary: owner ? (labelsMatch(primary, ownerName) ? null : ownerName) : 'Unassigned',
        domain: (property as any)?.domain || null,
        kind: 'site' as const,
      }
    }

    if (viewingSite && selectedProperty) {
      const primary = sitePrimaryLabel(selectedProperty)
      const secondary = labelsMatch(primary, workspaceName) ? null : workspaceName
      return {
        primary,
        secondary,
        domain: selectedProperty.domain || null,
        kind: 'site' as const,
      }
    }

    // Workspace-level / All Sites
    const siteCount = enabledProperties.length
    return {
      primary: workspaceName,
      secondary: siteCount > 0
        ? `${siteCount} site${siteCount === 1 ? '' : 's'}`
        : 'No sites',
      domain: null as string | null,
      kind: 'workspace' as const,
    }
  }, [
    isAccountRoute,
    isPropertyRoute,
    viewingSite,
    selectedProperty,
    currentUnassignedProperty,
    currentPropertyIdFromRoute,
    workspacesData,
    workspaceName,
    enabledProperties.length,
    totalSiteCount,
    workspaces.length,
  ])

  const triggerGlyph = (() => {
    if (trigger.kind === 'account') return <AccountMark size={28} />
    if (trigger.kind === 'site' && trigger.domain && isDomainLike(trigger.domain)) {
      return <SiteFavicon domain={trigger.domain} size={28} />
    }
    if (trigger.kind === 'site' && trigger.primary && isDomainLike(trigger.primary)) {
      return <SiteFavicon domain={trigger.primary} size={28} />
    }
    return <WorkspaceMark name={workspaceName} size={28} />
  })()

  const tooltipLabel = trigger.secondary
    ? `${trigger.primary} · ${trigger.secondary}`
    : trigger.primary

  const handleWorkspaceSelect = (workspace: WorkspaceDropdownItem) => {
    const slug = workspace.slug || workspace.id
    navigate(`/workspace/${slug}/traffic`)
  }

  const handleCreateWorkspace = () => {
    navigate('/workspace/new')
  }

  const handleViewAllWorkspaces = () => {
    navigate('/dashboard')
  }

  const handlePropertySelect = (propertyId: string | null) => {
    setSelectedPropertyId?.(propertyId)
  }

  const handleUnassignedPropertySelect = (property: any) => {
    const slugId = generateSlugId(property.name || property.domain, property.propertyId)
    navigate(`/p/${slugId}${currentSubPath}`)
  }

  const PropertyRow = ({
    domain,
    name,
    isSelected,
    onClick,
  }: {
    domain: string
    name: string
    isSelected: boolean
    onClick: () => void
  }) => (
    <DropdownMenuItem
      onClick={onClick}
      className={cn(
        "cursor-pointer py-1.5 px-2.5 rounded-lg transition-colors duration-100 flex items-center gap-2.5",
        isSelected
          ? "bg-muted/50 dark:bg-muted/30"
          : "hover:bg-muted/40 dark:hover:bg-muted/20"
      )}
    >
      <RowFavicon domain={domain || name} size={16} />
      <span className={cn(
        "text-xs truncate leading-tight flex-1 min-w-0",
        isSelected ? "font-medium text-foreground" : "text-foreground/70"
      )}>
        {domain || name || 'Untitled'}
      </span>
      {isSelected && (
        <HugeiconsIcon icon={Tick02Icon} size={14} className="text-foreground/50 shrink-0" />
      )}
    </DropdownMenuItem>
  )

  const WorkspaceRow = ({ workspace }: { workspace: WorkspaceDropdownItem }) => {
    const props = workspace.properties ?? []
    const previewProps = props.slice(0, 3)
    const overflow = props.length - previewProps.length
    return (
      <DropdownMenuItem
        key={workspace.id}
        onClick={() => handleWorkspaceSelect(workspace)}
        className="cursor-pointer py-1.5 px-2.5 rounded-lg transition-colors duration-100 gap-2 group hover:bg-muted/40 dark:hover:bg-muted/20 flex items-center justify-between"
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <WorkspaceMark name={workspace.name} size={18} />
          <span className="text-xs font-medium text-foreground/80 truncate leading-tight">
            {workspace.name}
          </span>
        </div>
        {props.length > 0 && (
          <div className="flex items-center gap-1 shrink-0">
            <div className="flex -space-x-1">
              {previewProps.map((p) => (
                <div
                  key={p.propertyId}
                  className="size-3.5 rounded-[3px] overflow-hidden ring-1 ring-popover bg-card"
                  title={p.domain || p.name}
                >
                  <Favicon
                    domain={p.domain}
                    size={14}
                    renderFallback={() => (
                      <SiteGradientAvatar domain={p.domain} color={getDomainColor(p.domain)} size={14} />
                    )}
                  />
                </div>
              ))}
            </div>
            {overflow > 0 && (
              <span className="text-xs text-muted-foreground/60 tabular-nums ml-0.5">
                +{overflow}
              </span>
            )}
          </div>
        )}
      </DropdownMenuItem>
    )
  }

  const SectionLabel = ({ children }: { children: React.ReactNode }) => (
    <div className="px-3 pt-2.5 pb-1">
      <span className="text-xs font-medium text-muted-foreground/50">
        {children}
      </span>
    </div>
  )

  const dropdownContent = (
    <div className="flex flex-col max-h-[80vh]">
      {/* Context strip — workspace settings (icon + tooltip). */}
      {!isPropertyRoute && !isAccountRoute && (currentWorkspace || currentWorkspaceId) && (
        <div className="flex items-center justify-between gap-2 px-2.5 pt-2 pb-0.5">
          <div className="flex items-center gap-2 min-w-0">
            <WorkspaceMark name={workspaceName} size={18} />
            <span className="text-xs font-medium text-foreground/80 truncate">
              {workspaceName}
            </span>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  e.stopPropagation()
                  const slugId = generateSlugId(workspaceName, currentWorkspace?.workspaceId || currentWorkspaceId || '')
                  navigate(`/workspace/${slugId}/settings`)
                }}
                aria-label="Workspace settings"
              >
                <HugeiconsIcon icon={Settings01Icon} size={14} strokeWidth={2} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left" className="text-xs font-medium">
              Workspace settings
            </TooltipContent>
          </Tooltip>
        </div>
      )}

      <div className="px-2 pt-1.5 pb-2 border-b border-border/30">
        <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-border/30 bg-muted/20">
          <HugeiconsIcon icon={Search01Icon} size={12} className="text-muted-foreground/50 shrink-0" />
          <input
            type="text"
            placeholder="Search workspaces and sites…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-transparent text-xs text-foreground placeholder:text-muted-foreground/40 outline-none w-full"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">

      {isPropertyRoute && (() => {
        const q = search.toLowerCase()
        const filteredUnassigned = q
          ? unassignedProperties.filter((p: any) =>
              (p.domain || '').toLowerCase().includes(q) ||
              (p.name || '').toLowerCase().includes(q)
            )
          : unassignedProperties
        if (filteredUnassigned.length === 0) return null
        return (
          <>
            <SectionLabel>Properties</SectionLabel>
            <div className="px-1.5 pb-1 space-y-0.5">
              {filteredUnassigned.map((property: any) => (
                <PropertyRow
                  key={property.propertyId}
                  domain={property.domain}
                  name={property.name || property.domain}
                  isSelected={property.propertyId === currentPropertyIdFromRoute}
                  onClick={() => handleUnassignedPropertySelect(property)}
                />
              ))}
            </div>
            <div className="mx-3 border-t border-border/30" />
          </>
        )
      })()}

      {!isPropertyRoute && !isAccountRoute && enabledProperties.length > 0 && (filteredProperties.length > 0 || !search) && (
        <>
          <SectionLabel>Properties</SectionLabel>
          <div className="px-1.5 pb-1 space-y-0.5">
            {enabledProperties.length > 1 && (
              <DropdownMenuItem
                onClick={() => handlePropertySelect(null)}
                className={cn(
                  "cursor-pointer py-1.5 px-2.5 rounded-lg transition-colors duration-100 flex items-center gap-2.5",
                  isAggregateMode || !selectedPropertyId
                    ? "bg-muted/50 dark:bg-muted/30"
                    : "hover:bg-muted/40 dark:hover:bg-muted/20"
                )}
              >
                <div className="size-4 rounded-[4px] flex items-center justify-center bg-muted/50 border border-border/30 shrink-0">
                  <HugeiconsIcon icon={Layers01Icon} size={10} className="text-muted-foreground/70" />
                </div>
                <span className={cn(
                  "text-xs truncate leading-tight flex-1",
                  (isAggregateMode || !selectedPropertyId) ? "font-medium text-foreground" : "text-foreground/70"
                )}>
                  All Sites ({enabledProperties.length})
                </span>
                {(isAggregateMode || !selectedPropertyId) && (
                  <HugeiconsIcon icon={Tick02Icon} size={14} className="text-foreground/50 shrink-0" />
                )}
              </DropdownMenuItem>
            )}
            {filteredProperties.map((property) => (
              <PropertyRow
                key={property.propertyId}
                domain={property.domain}
                name={property.name || property.domain}
                isSelected={selectedPropertyId === property.propertyId}
                onClick={() => handlePropertySelect(property.propertyId)}
              />
            ))}
          </div>
          <div className="mx-3 border-t border-border/30" />
        </>
      )}

      {(() => {
        const otherWorkspaces = isPropertyRoute || isAccountRoute
          ? workspaces
          : workspaces.filter(w => w.id !== currentWorkspaceId)
        if (otherWorkspaces.length === 0) return null
        const query = search.toLowerCase()
        const filtered = query
          ? otherWorkspaces.filter(w => w.name.toLowerCase().includes(query))
          : otherWorkspaces
        if (filtered.length === 0) return null
        const sectionLabel = isAccountRoute || isPropertyRoute ? 'Workspaces' : 'Other Workspaces'
        return (
          <>
            <SectionLabel>{sectionLabel}</SectionLabel>
            <div className="px-1.5 pb-1 space-y-0.5">
              {filtered.map((workspace) => (
                <WorkspaceRow key={workspace.id} workspace={workspace} />
              ))}
            </div>
            <div className="mx-3 border-t border-border/30" />
          </>
        )
      })()}

      {allPropertiesData && allPropertiesData.length > 0 && search && (
        (() => {
          const q = search.toLowerCase()
          const allProps = allPropertiesData.filter((p: any) =>
            (p.domain || '').toLowerCase().includes(q) ||
            (p.name || '').toLowerCase().includes(q)
          )
          if (allProps.length === 0) return null
          return (
            <>
              <SectionLabel>All properties</SectionLabel>
              <div className="px-1.5 pb-1 space-y-0.5">
                {allProps.map((property: any) => (
                  <DropdownMenuItem
                    key={property.propertyId}
                    onClick={() => {
                      const ownerWorkspace = workspacesData.find(w =>
                        w.properties?.some((p: any) => p.propertyId === property.propertyId)
                      )
                      if (ownerWorkspace) {
                        const wsSlugId = generateSlugId(ownerWorkspace.name || ownerWorkspace.workspaceId, ownerWorkspace.workspaceId)
                        navigate(`/workspace/${wsSlugId}${currentSubPath}?site=${property.propertyId}`)
                      } else {
                        const slugId = generateSlugId(property.name || property.domain, property.propertyId)
                        navigate(`/p/${slugId}${currentSubPath}`)
                      }
                    }}
                    className="cursor-pointer py-1.5 px-2.5 rounded-lg transition-colors duration-100 hover:bg-muted/40 dark:hover:bg-muted/20 flex items-center gap-2.5"
                  >
                    <RowFavicon domain={property.domain || property.name} size={16} />
                    <div className="flex flex-col min-w-0 flex-1 gap-0">
                      <span className="text-xs font-medium text-foreground/80 truncate leading-tight">
                        {property.domain || property.name || 'Untitled'}
                      </span>
                      {property.domain && property.name && !labelsMatch(property.domain, property.name) && (
                        <span className="text-xs text-muted-foreground/50 truncate leading-tight">
                          {property.name}
                        </span>
                      )}
                    </div>
                  </DropdownMenuItem>
                ))}
              </div>
              <div className="mx-3 border-t border-border/30" />
            </>
          )
        })()
      )}

      {search && filteredProperties.length === 0 && (() => {
        const q = search.toLowerCase()
        const otherWorkspaces = isPropertyRoute
          ? workspaces
          : workspaces.filter(w => w.id !== currentWorkspaceId)
        const wsMatches = otherWorkspaces.filter(w => w.name.toLowerCase().includes(q))
        const allMatches = (allPropertiesData || []).filter((p: any) =>
          (p.domain || '').toLowerCase().includes(q) ||
          (p.name || '').toLowerCase().includes(q)
        )
        if (wsMatches.length === 0 && allMatches.length === 0) {
          return (
            <p className="text-xs text-muted-foreground/50 text-center py-6">
              No workspaces or sites match "{search}"
            </p>
          )
        }
        return null
      })()}

      </div>

      <div className="p-1.5 space-y-0.5 border-t border-border/30 bg-popover">
        <DropdownMenuItem
          onClick={handleViewAllWorkspaces}
          className="cursor-pointer py-2 px-2.5 rounded-md gap-2.5 hover:bg-muted/40 dark:hover:bg-muted/20 transition-colors duration-100"
        >
          <HugeiconsIcon icon={GridViewIcon} size={16} strokeWidth={2} className="text-muted-foreground/50 shrink-0" />
          <span className="text-xs font-medium text-foreground/80">View all workspaces</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={handleCreateWorkspace}
          className="cursor-pointer py-2 px-2.5 rounded-md gap-2.5 hover:bg-muted/40 dark:hover:bg-muted/20 transition-colors duration-100"
        >
          <HugeiconsIcon icon={PlusSignIcon} size={16} strokeWidth={2} className="text-muted-foreground/50 shrink-0" />
          <span className="text-xs font-medium text-foreground/80">Create workspace</span>
        </DropdownMenuItem>
      </div>
    </div>
  )

  if (isCollapsed) {
    return (
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex size-10 mx-auto shrink-0 items-center justify-center rounded-lg hover:bg-black/[0.035] dark:hover:bg-white/5 outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring/60"
                aria-label={tooltipLabel}
              >
                {triggerGlyph}
              </button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={8} className="text-xs font-medium max-w-[220px]">
            {tooltipLabel}
          </TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="start" side="right" className="w-[280px] p-0 border border-border/30 shadow-md rounded-lg">
          {dropdownContent}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {/* Paper "App v3": 40px row, 6px side padding, 10px gap, 28px mark */}
        <button
          type="button"
          className={cn(
            "flex w-full h-10 items-center gap-2.5 px-1.5 rounded-lg text-left",
            "transition-colors duration-150 hover:bg-black/[0.035] dark:hover:bg-white/5",
            "data-[state=open]:bg-black/[0.035] dark:data-[state=open]:bg-white/5",
            "outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring/60"
          )}
        >
          {triggerGlyph}
          <div className="flex flex-col min-w-0 flex-1 gap-px">
            <span className="truncate text-[13px] font-semibold leading-4 tracking-[-0.01em] text-foreground">
              {trigger.primary}
            </span>
            {trigger.secondary && (
              <span className="truncate text-xs leading-4 text-foreground/60">
                {trigger.secondary}
              </span>
            )}
          </div>
          <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" className="shrink-0 stroke-foreground/35">
            <path d="m7 15 5 5 5-5M7 9l5-5 5 5" fill="none" strokeWidth="2.14" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[280px] p-0 border border-border/30 shadow-md rounded-xl">
        {dropdownContent}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
